// Analítica Global — EXCLUSIVO del administrador global (system_admin).
//
// Muestra información relevante de TODOS los tenants de la plataforma, con un
// filtro OPCIONAL por cuenta (tenant). Es SEPARADA de la analítica que cada
// cuenta tiene en su propio catálogo:
//   - Sin cuenta seleccionada → agrega sobre todas las cuentas.
//   - Con una cuenta          → acota a esa cuenta (igual que su vista propia).
//
// Consume `POST /admin/analytics` (cross-tenant en backend). Reutiliza el mismo
// contrato de dimensiones/métricas que la analítica por cuenta, más la dimensión
// "cuenta" para desglosar por tenant cuando se ven todas.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import type { AnalyticsQueryBody } from '../api/endpoints.js';
import { AlertMessage, AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type { AnalyticsResult } from '../types.js';

/** Formatea un valor monetario con separador de miles y 2 decimales. */
function money(value: number | string | null | undefined): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function format(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return v;
}

function csvCell(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Claves de dimensiones agrupables. "tenant" solo aplica en la vista global. */
const DIMENSION_KEYS = [
  'tenant', 'genre', 'subgenre', 'artist', 'label', 'origin',
  'type', 'media_condition', 'decade', 'year',
] as const;

/** Claves de métricas (alineadas con la lista blanca del backend). */
const METRIC_KEYS = [
  'count_records', 'total_vinyl', 'distinct_artists',
  'sum_value', 'avg_value', 'sum_discogs_mid', 'avg_discogs_mid',
] as const;

const VALUE_METRICS = new Set(['sum_value', 'avg_value', 'sum_discogs_mid', 'avg_discogs_mid']);

export function GlobalAnalyticsView() {
  const { t } = useTranslation();
  const api = useApi();
  // Etiqueta legible de una columna (dimensión o métrica) por su clave.
  const colLabel = (key: string): string => {
    if ((DIMENSION_KEYS as readonly string[]).includes(key)) return t(`globalAnalytics.dim.${key}`);
    if ((METRIC_KEYS as readonly string[]).includes(key)) return t(`globalAnalytics.metric.${key}`);
    return key;
  };
  // Cuentas para el selector de tenant (filtro).
  const accounts = useAsync(() => api.admin.listAccounts(), []);
  const [tenantId, setTenantId] = useState('');

  const accountList = accounts.data?.items ?? [];
  const tenantOptions = useMemo(() => {
    const byTenant = new Map<string, { name: string; email: string; isOwner: boolean }>();
    for (const a of accountList) {
      const prev = byTenant.get(a.tenant_id);
      const isOwner = a.role === 'owner';
      if (!prev || (isOwner && !prev.isOwner)) {
        byTenant.set(a.tenant_id, { name: a.tenant_name ?? a.tenant_id, email: a.email, isOwner });
      }
    }
    return [...byTenant.entries()].map(([id, v]) => ({
      id,
      label: v.email ? `${v.name} — ${v.email}` : v.name,
    }));
  }, [accountList]);

  // Resumen de totales (recarga al cambiar la cuenta seleccionada).
  const summary = useAsync(
    async () => {
      const totals = await api.admin.analytics({
        tenant_id: tenantId || undefined,
        group_by: [],
        metrics: ['count_records', 'total_vinyl', 'distinct_artists', 'sum_value', 'sum_discogs_mid'],
      });
      const row = totals.rows[0] ?? {};
      return {
        albums: Number(row.count_records ?? 0),
        vinyl: Number(row.total_vinyl ?? 0),
        artists: Number(row.distinct_artists ?? 0),
        totalReal: Number(row.sum_value ?? 0),
        totalDiscogs: Number(row.sum_discogs_mid ?? 0),
      };
    },
    [tenantId],
  );

  // Constructor de vista.
  const [groupBy, setGroupBy] = useState<string[]>(['tenant']);
  const [metrics, setMetrics] = useState<string[]>(['count_records']);
  const [yearBasis, setYearBasis] = useState<'edition' | 'original'>('edition');
  const [limit, setLimit] = useState(50);
  const [result, setResult] = useState<AnalyticsResult | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(list: string[], setList: (v: string[]) => void, key: string) {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  }

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const body: AnalyticsQueryBody & { tenant_id?: string } = {
        tenant_id: tenantId || undefined,
        group_by: groupBy,
        metrics,
        year_basis: yearBasis,
        limit,
      };
      const res = await api.admin.analytics(body);
      setResult(res);
      setSortCol(res.metrics[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('globalAnalytics.errQuery'));
    } finally {
      setBusy(false);
    }
  }, [api, tenantId, groupBy, metrics, yearBasis, limit]);

  const columns = useMemo(() => (result ? [...result.group_by, ...result.metrics] : []), [result]);

  const sortedRows = useMemo(() => {
    if (!result) return [];
    const rows = [...result.rows];
    if (sortCol) {
      rows.sort((a, b) => {
        const av = a[sortCol];
        const bv = b[sortCol];
        const an = typeof av === 'number' ? av : Number(av);
        const bn = typeof bv === 'number' ? bv : Number(bv);
        const cmp =
          Number.isFinite(an) && Number.isFinite(bn)
            ? an - bn
            : String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [result, sortCol, sortDir]);

  const chartMetric = result?.metrics[0];
  const chartDim = result?.group_by[0];
  const maxVal = useMemo(() => {
    if (!result || !chartMetric) return 0;
    return Math.max(0, ...result.rows.map((r) => Number(r[chartMetric] ?? 0)));
  }, [result, chartMetric]);

  function exportCsv() {
    if (!result) return;
    const header = columns.join(',');
    const lines = sortedRows.map((r) => columns.map((c) => csvCell(r[c])).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analitica-global.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  }

  const scope = tenantId ? t('globalAnalytics.scopeThis') : t('globalAnalytics.scopeAll');

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('globalAnalytics.pageTitle')}
        subtitle={t('globalAnalytics.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />

        {/* Filtro por cuenta (tenant). */}
        <div className="field-slot">
          <Icon name="library_music" className="text-on-surface-variant text-[18px] mr-space-xs" />
          <select
            className="field-input"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            aria-label={t('globalAnalytics.filterByAccount')}
          >
            <option value="">{t('globalAnalytics.allAccounts')}</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Resumen de totales del ámbito seleccionado. */}
        <Card title={t('globalAnalytics.summaryTitle', { scope })} icon="insights">
          <AsyncBoundary state={summary}>
            {(s) => (
              <div className="grid grid-cols-2 gap-space-sm">
                <Metric icon="album" label={t('globalAnalytics.mAlbums')} value={s.albums.toLocaleString()} tone="glow" />
                <Metric icon="library_music" label={t('globalAnalytics.mVinyl')} value={s.vinyl.toLocaleString()} tone="glow" />
                <Metric icon="groups" label={t('globalAnalytics.mArtists')} value={s.artists.toLocaleString()} tone="glow" />
                <Metric icon="savings" label={t('globalAnalytics.mRealValue')} value={money(s.totalReal)} tone="glow" />
                <Metric icon="trending_up" label={t('globalAnalytics.mDiscogsValue')} value={money(s.totalDiscogs)} tone="mint" />
              </div>
            )}
          </AsyncBoundary>
        </Card>

        {/* Constructor de vista. */}
        <Card title={t('globalAnalytics.viewBuilder')} icon="dashboard_customize">
          <span className="section-label text-vacuum-glow">{t('globalAnalytics.groupBy')}</span>
          <div className="flex flex-wrap gap-space-xs mt-space-xs mb-space-sm">
            {DIMENSION_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`chip ${groupBy.includes(key) ? 'chip-active' : ''}`}
                onClick={() => toggle(groupBy, setGroupBy, key)}
              >
                {groupBy.includes(key) && <Icon name="check" className="text-[14px]" />} {t(`globalAnalytics.dim.${key}`)}
              </button>
            ))}
          </div>

          <span className="section-label text-vacuum-glow">{t('globalAnalytics.metrics')}</span>
          <div className="flex flex-wrap gap-space-xs mt-space-xs mb-space-sm">
            {METRIC_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`chip ${metrics.includes(key) ? 'chip-active' : ''}`}
                onClick={() => toggle(metrics, setMetrics, key)}
              >
                {metrics.includes(key) && <Icon name="check" className="text-[14px]" />} {t(`globalAnalytics.metric.${key}`)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-space-sm mt-space-xs">
            <div className="flex flex-col gap-space-xs">
              <label className="field-label">{t('globalAnalytics.yearBasis')}</label>
              <div className="field-slot">
                <select
                  className="field-input"
                  value={yearBasis}
                  onChange={(e) => setYearBasis(e.target.value as 'edition' | 'original')}
                >
                  <option value="edition">{t('globalAnalytics.yearBasisEdition')}</option>
                  <option value="original">{t('globalAnalytics.yearBasisOriginal')}</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-space-xs">
              <label className="field-label">{t('globalAnalytics.topN')}</label>
              <div className="field-slot">
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          <button
            className="btn-primary w-full mt-space-md"
            type="button"
            disabled={busy || groupBy.length === 0}
            onClick={() => void run()}
          >
            <Icon name="analytics" className="text-[18px]" />
            {busy ? t('globalAnalytics.calculating') : t('globalAnalytics.calculate')}
          </button>
        </Card>

        {result && (
          <Card
            title={t('globalAnalytics.resultTitle', { count: result.rows.length })}
            icon="monitoring"
            action={
              <button className="btn-ghost btn-sm" type="button" onClick={exportCsv}>
                <Icon name="download" className="text-[16px]" /> CSV
              </button>
            }
          >
            {chartMetric && chartDim && maxVal > 0 && (
              <div className="flex flex-col gap-space-xs mb-space-md">
                {sortedRows.slice(0, 12).map((r, i) => {
                  const val = Number(r[chartMetric] ?? 0);
                  return (
                    <div key={i} className="grid grid-cols-[110px_1fr_48px] gap-space-xs items-center">
                      <span
                        className="font-body-sm text-body-sm text-on-surface-variant truncate"
                        title={String(r[chartDim] ?? '')}
                      >
                        {String(r[chartDim] ?? '—')}
                      </span>
                      <div className="meter-track !h-4">
                        <div
                          className="h-full bg-primary-container rounded-full"
                          style={{ width: `${(val / maxVal) * 100}%` }}
                        />
                      </div>
                      <span className="font-code-catalog text-body-sm text-primary text-right">
                        {VALUE_METRICS.has(chartMetric) ? money(val) : val}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full font-code-catalog text-body-sm">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c}
                        className="text-left py-space-xs px-space-xs text-on-surface-variant uppercase cursor-pointer whitespace-nowrap"
                        onClick={() => {
                          if (sortCol === c) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          else {
                            setSortCol(c);
                            setSortDir('desc');
                          }
                        }}
                      >
                        {colLabel(c)}
                        {sortCol === c ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-on-surface">
                  {sortedRows.map((r, i) => (
                    <tr key={i} className="border-t border-border-subtle">
                      {columns.map((c) => (
                        <td key={c} className="py-space-xs px-space-xs whitespace-nowrap">
                          {VALUE_METRICS.has(c) ? money(r[c]) : format(r[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Tarjeta compacta de una métrica del resumen. */
function Metric({
  icon,
  label,
  value,
  tone,
}: {
  icon: string;
  label: string;
  value: string;
  tone: 'glow' | 'mint';
}) {
  return (
    <div className="surface-rack-card !p-space-md flex flex-col gap-space-xs">
      <span className="flex items-center gap-space-xs section-label text-on-surface-variant">
        <Icon name={icon} className="text-[16px] text-primary" /> {label}
      </span>
      <span
        className={`font-headline-md text-headline-md ${tone === 'mint' ? 'text-goldmine-mint' : 'text-vacuum-glow'}`}
      >
        {value}
      </span>
    </div>
  );
}
